/* ═══════════════════════════════════════════════════════════════════════════
 * ACTIVATION D'UN COMPTE — LE PORTEUR CHOISIT SON MOT DE PASSE
 * ═══════════════════════════════════════════════════════════════════════════
 * (Lot du 12/10/2026.)
 *
 * 🔴 CET ÉCRAN N'EXISTAIT PAS, ET TOUTE LA CHAÎNE EN DÉPENDAIT. Mesuré en
 *    production le 12/10 : la route `POST /api/auth/activation` existe depuis le
 *    17/09, `public.activation_emettre` et `activation_consommer` existent, et
 *    le bundle servi ne contenait **aucune** occurrence de « activation/ » —
 *    donc aucune page pour ouvrir un lien. `public.activations_compte` comptait
 *    **0 ligne** : aucun lien n'avait jamais été émis.
 *    ➡️ Le produit avait remplacé « l'administrateur tape le mot de passe » par
 *       « le porteur l'ouvre lui-même », et la seconde moitié manquait. C'est
 *       pourquoi plus aucun compte ne pouvait être créé.
 *
 * ⛔ AUCUNE SESSION N'EST OUVERTE ICI. Le serveur n'en ouvre pas, et l'écran ne
 *    fait pas semblant : on active, puis on se connecte par le chemin normal.
 *    *Un lien qui connecterait serait un équivalent de mot de passe — et il est
 *    transmis par un canal quelconque.*
 *
 * ⚠️ LE REFUS EST LE MÊME pour un jeton inconnu, expiré, consommé ou révoqué, et
 *    l'écran n'essaie pas de deviner lequel : la distinction vit dans la
 *    fonction SQL et n'en sort jamais. *Deux messages différents diraient à un
 *    inconnu si le compte existe.*
 *
 * ⓘ La politique de mot de passe est VÉRIFIÉE PAR LE SERVEUR
 *   (`utils/passwordPolicy`). Les contrôles ci-dessous ne sont là que pour
 *   éviter un aller-retour inutile et guider la saisie — ils ne font pas foi, et
 *   le message qui s'affiche en cas de refus est celui du serveur.
 * ═══════════════════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from 'services/api';

export default function ActivationPage() {
  const { jeton } = useParams();
  const navigate = useNavigate();
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');
  const [fait, setFait] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [voir, setVoir] = useState(false);

  /* ⓘ Les mêmes quatre règles qu'ailleurs dans l'écran — min. 8, une majuscule,
   *   une minuscule, un chiffre. Elles sont affichées, pas seulement vérifiées :
   *   une exigence qu'on découvre en échouant est une exigence mal posée. */
  const regles = [
    { t: 'Au moins 8 caractères', ok: motDePasse.length >= 8 },
    { t: 'Une lettre majuscule', ok: /[A-Z]/.test(motDePasse) },
    { t: 'Une lettre minuscule', ok: /[a-z]/.test(motDePasse) },
    { t: 'Un chiffre', ok: /[0-9]/.test(motDePasse) },
  ];
  const toutesOk = regles.every((r) => r.ok);
  const concordent = motDePasse.length > 0 && motDePasse === confirmation;

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur('');
    if (!toutesOk) return setErreur('Le mot de passe ne respecte pas encore toutes les règles.');
    if (!concordent) return setErreur('Les deux saisies ne correspondent pas.');
    setEnvoi(true);
    try {
      await api.post('/auth/activation', { jeton, nouveau_mot_de_passe: motDePasse });
      setFait(true);
    } catch (err) {
      setErreur(err.response?.data?.error
        || 'Ce lien ne peut pas être utilisé. Demandez-en un nouveau à votre administrateur.');
    }
    setEnvoi(false);
  };

  const carte = {
    background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
    padding: '2.5rem 2rem', maxWidth: 440, width: '100%', margin: '1rem',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    }}>
      <div style={carte}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: 'rgba(139,26,26,0.08)',
            margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <h1 style={{ fontSize: '1.3rem', margin: 0 }}>
            {fait ? 'Compte activé' : 'Choisissez votre mot de passe'}
          </h1>
          {!fait && (
            <div style={{ fontSize: 13, color: '#666', marginTop: 8, lineHeight: 1.5 }}>
              Ce mot de passe est le vôtre : personne d&rsquo;autre ne le connaîtra,
              pas même l&rsquo;administrateur qui a créé votre compte.
            </div>
          )}
        </div>

        {fait ? (
          <div>
            <div style={{
              background: 'rgba(26,135,84,0.08)', color: '#1A8754', borderRadius: 8,
              padding: '0.9rem 1rem', fontSize: 13, lineHeight: 1.5, marginBottom: '1.25rem',
            }}>
              Votre mot de passe est enregistré. Connectez-vous maintenant avec
              votre adresse e-mail et ce mot de passe.
            </div>
            <button type="button" className="gss-btn gss-btn-primary"
              style={{ width: '100%' }} onClick={() => navigate('/login')}>
              Aller à la connexion
            </button>
          </div>
        ) : (
          <form onSubmit={soumettre}>
            {erreur && (
              <div style={{
                background: 'rgba(220,38,38,0.08)', color: '#DC2626', borderRadius: 8,
                padding: '0.75rem 1rem', fontSize: 13, marginBottom: '1rem', lineHeight: 1.5,
              }}>
                {erreur}
              </div>
            )}

            <div className="gss-field">
              <label className="gss-label">Nouveau mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input className="gss-input" type={voir ? 'text' : 'password'} autoFocus
                  value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)}
                  style={{ paddingRight: 38 }} />
                <button type="button" onClick={() => setVoir((v) => !v)}
                  title={voir ? 'Masquer' : 'Afficher'}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#888',
                  }}>
                  {voir ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
            </div>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0.6rem 0 1rem', fontSize: 12.5 }}>
              {regles.map((r) => (
                <li key={r.t} style={{ color: r.ok ? '#1A8754' : '#888', marginBottom: 3 }}>
                  {r.ok ? '✓' : '·'} {r.t}
                </li>
              ))}
            </ul>

            <div className="gss-field">
              <label className="gss-label">Confirmer</label>
              <input className="gss-input" type={voir ? 'text' : 'password'}
                value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
              {confirmation.length > 0 && !concordent && (
                <small style={{ color: '#B45309' }}>Les deux saisies ne correspondent pas.</small>
              )}
            </div>

            <button type="submit" className="gss-btn gss-btn-primary"
              style={{ width: '100%', marginTop: '1.25rem' }}
              disabled={envoi || !toutesOk || !concordent}>
              {envoi ? 'Enregistrement...' : 'Activer mon compte'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
